import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api/axios';
import toast from 'react-hot-toast';
import { 
  MdAccountBalanceWallet, MdTrendingUp, MdMonetizationOn, 
  MdCheckCircle, MdPendingActions, MdPayment, MdArrowBack 
} from 'react-icons/md';
import { 
  ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend 
} from 'recharts';
import GlobalDateFilter from '../components/GlobalDateFilter';
import Pagination from '../components/Pagination';
import { MdSearch } from 'react-icons/md';
import { formatDate, formatDateTime, formatINR } from '../utils/format';

export default function ContributorEarnings() {
  const navigate = useNavigate();
  const [selectedContributorId, setSelectedContributorId] = useState('');
  const [allContributors, setAllContributors] = useState([]);

  // Data states
  const [contributor, setContributor] = useState(null);
  const [wallet, setWallet] = useState(null);
  const [withdrawals, setWithdrawals] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [topArticles, setTopArticles] = useState([]);

  // Overview states
  const [overviewContributors, setOverviewContributors] = useState([]);
  const [overviewPagination, setOverviewPagination] = useState({ page: 1, pages: 1, total: 0 });
  const [overviewSearch, setOverviewSearch] = useState('');
  const [overviewSort, setOverviewSort] = useState('wallet_desc');
  const [overviewWalletFilter, setOverviewWalletFilter] = useState('all');
  const [overviewLoading, setOverviewLoading] = useState(true);

  // Fetch overview data
  const fetchOverview = async (page = 1) => {
    try {
      setOverviewLoading(true);
      const res = await api.get('/admin/contributors/earnings-overview', {
        params: { search: overviewSearch, page, limit: 20, sort: overviewSort, wallet_filter: overviewWalletFilter }
      });
      setOverviewContributors(res.data.data);
      setOverviewPagination(res.data.pagination);
    } catch (err) {
      toast.error('Failed to load contributors overview');
    } finally {
      setOverviewLoading(false);
    }
  };

  useEffect(() => {
    if (!selectedContributorId) {
      const delay = setTimeout(() => fetchOverview(1), 500);
      return () => clearTimeout(delay);
    }
  }, [overviewSearch, overviewSort, overviewWalletFilter, selectedContributorId]);

  // UI states
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [dateFilter, setDateFilter] = useState('last30');
  const [customRange, setCustomRange] = useState({ from: '', to: '' });



  useEffect(() => {
    if (!selectedContributorId) {
      setLoading(false);
      return;
    }
    
    setLoading(true);
    setRefreshing(true);
    const query = `?filter=${dateFilter}&from_date=${customRange.from}&to_date=${customRange.to}`;

    Promise.all([
      api.get(`/admin/contributors/${selectedContributorId}`),
      api.get(`/admin/contributors/${selectedContributorId}/wallet`),
      api.get(`/admin/contributors/${selectedContributorId}/withdrawals`),
      api.get(`/admin/contributors/${selectedContributorId}/earnings-analytics${query}`),
      api.get(`/admin/contributors/${selectedContributorId}/top-earning-articles`),
    ]).then(([contRes, wallRes, withRes, anaRes, topRes]) => {
      setContributor(contRes.data.data);
      setWallet(wallRes.data.data);
      setWithdrawals(withRes.data.data);
      setAnalytics(anaRes.data.data);
      setTopArticles(topRes.data.data);
    }).catch(() => toast.error('Failed to load earnings data'))
      .finally(() => { setLoading(false); setRefreshing(false); });
  }, [selectedContributorId, dateFilter, customRange.from, customRange.to]);

  if (loading) return <div className="page-loader">Loading...</div>;

  const stats = wallet?.stats || {};
  const ledger = wallet?.ledger || [];
  
  // Computed values based on mockup logic
  const lifetimeEarnings = stats.income_till_date || 0;
  const availableBalance = stats.available_balance || 0;
  const totalPaid = withdrawals.filter(w => w.status === 'paid').reduce((acc, curr) => acc + Number(curr.amount), 0);
  
  // "Pending Earnings" = Not a standard field. We approximate it by taking accepted articles that aren't credited yet, or 0.
  // For UI matching purposes, we'll set it dynamically based on pending withdrawals if not provided, but usually it's separate.
  // We'll use stats.pending * 150 as a placeholder for "Pending (unreviewed) Earnings potential"
  const pendingEarnings = (stats.pending || 0) * 150; 
  
  const pendingRequestsCount = withdrawals.filter(w => w.status === 'requested' || w.status === 'processing').length;
  const pendingRequestsAmount = stats.pending_withdrawal || 0;
  
  const completedPayoutsCount = withdrawals.filter(w => w.status === 'paid').length;

  const payoutInfo = contributor?.payout || {};

  // Formatter for transaction types
  const formatTxType = (type, refType) => {
    if (refType === 'article') return <span className="badge badge-success">Article Reward</span>;
    if (refType === 'withdrawal') return <span className="badge badge-danger">Payout Request</span>;
    if (refType === 'adjustment') return <span className="badge badge-warning">Adjustment</span>;
    return <span className="badge">{refType}</span>;
  };

  // Build chart data
  const chartDataMap = {};
  if (analytics?.daily) {
    analytics.daily.articles.forEach(d => { chartDataMap[d.date] = { ...chartDataMap[d.date], date: d.date, articles: d.articles } });
    analytics.daily.reads.forEach(d => { chartDataMap[d.date] = { ...chartDataMap[d.date], date: d.date, reads: d.reads } });
    analytics.daily.earnings.forEach(d => { chartDataMap[d.date] = { ...chartDataMap[d.date], date: d.date, earnings: Number(d.earnings) } });
  }
  const chartData = Object.values(chartDataMap).sort((a,b) => new Date(a.date) - new Date(b.date));

  return (
    <div className="page-container" style={{ opacity: refreshing ? 0.6 : 1, transition: 'opacity 0.2s' }}>
      <div className="page-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: 15 }}>
          {selectedContributorId && (
            <button className="btn btn-icon" onClick={() => setSelectedContributorId('')}>
              <MdArrowBack size={24} />
            </button>
          )}
          <div>
            <h1 style={{ margin: 0 }}>Contributor Earnings</h1>
            <p className="text-muted" style={{ margin: '4px 0 0', fontSize: 13 }}>
              Overview of contributor earnings, wallet balances, and payouts
            </p>
          </div>
        </div>
        <div className="header-actions">
          {selectedContributorId && (
            <GlobalDateFilter 
              filter={dateFilter} 
              fromDate={customRange.from} 
              toDate={customRange.to}
              onChange={(f, from, to) => {
                setDateFilter(f);
                setCustomRange({ from, to });
              }}
            />
          )}
        </div>
      </div>

      {!selectedContributorId ? (
        <div className="card">
          <div className="filter-bar" style={{ marginBottom: 20 }}>
            <div className="search-input-wrap">
              <MdSearch className="search-icon" />
              <input
                type="text"
                value={overviewSearch}
                onChange={(e) => setOverviewSearch(e.target.value)}
                placeholder="Search contributors by name, email, or phone..."
                className="search-input"
              />
            </div>
            <select value={overviewWalletFilter} onChange={(e) => setOverviewWalletFilter(e.target.value)} style={{ marginLeft: 10 }}>
              <option value="all">All Wallet Balances</option>
              <option value="0-100">₹0 – ₹100</option>
              <option value="101-500">₹101 – ₹500</option>
              <option value="501-1000">₹501 – ₹1,000</option>
              <option value="above_1000">Above ₹1,000</option>
            </select>
            <select value={overviewSort} onChange={(e) => setOverviewSort(e.target.value)} style={{ marginLeft: 10 }}>
              <option value="wallet_desc">Highest Wallet Balance</option>
              <option value="wallet_asc">Lowest Wallet Balance</option>
              <option value="newest">Newest First</option>
            </select>
          </div>

          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>Contributor</th>
                  <th>Phone Number</th>
                  <th>Article Count</th>
                  <th>Wallet Balance</th>
                  <th>Total Earnings</th>
                  <th>Pending Payout</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {overviewLoading ? (
                  <tr><td colSpan="6" className="empty-row">Loading...</td></tr>
                ) : overviewContributors.length === 0 ? (
                  <tr><td colSpan="6" className="empty-row">No contributors found.</td></tr>
                ) : (
                  overviewContributors.map(c => (
                    <tr key={c.id}>
                      <td>
                        <strong>{c.name}</strong><br />
                        <span style={{ fontSize: 12, color: '#64748B' }}>{c.email}</span>
                      </td>
                      <td>{c.phone}</td>
                      <td>{c.article_count}</td>
                      <td><strong style={{ color: '#10B981' }}>{formatINR(c.wallet_balance)}</strong></td>
                      <td><strong style={{ color: '#2563EB' }}>{formatINR(c.total_earnings)}</strong></td>
                      <td>{formatINR(c.pending_payout)}</td>
                      <td>
                        <button className="btn btn-sm btn-primary" onClick={() => setSelectedContributorId(c.id)}>View</button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          {overviewPagination.pages > 1 && (
            <Pagination pagination={overviewPagination} onPageChange={fetchOverview} />
          )}
        </div>
      ) : !contributor ? (
        <div className="empty-state">Contributor not found</div>
      ) : (
        <>
          <div style={{ background: '#fff', padding: '12px 20px', borderRadius: 8, marginBottom: 20, display: 'flex', alignItems: 'center', gap: 15, border: '1px solid #e2e8f0' }}>
        <img src={contributor.profile_image_url || `https://ui-avatars.com/api/?name=${encodeURIComponent(contributor.name)}&background=random`} alt="" style={{ width: 40, height: 40, borderRadius: '50%' }} />
        <div>
          <strong style={{ fontSize: 15 }}>{contributor.name}</strong>
          <div style={{ fontSize: 12, color: '#64748B' }}>{contributor.email}</div>
        </div>
        <span className={`badge ${contributor.status === 'approved' ? 'badge-success' : 'badge-warning'}`} style={{ marginLeft: 'auto' }}>
          {contributor.status}
        </span>
      </div>

      {/* Top Stats Cards */}
      <div className="dashboard-grid" style={{ marginBottom: 24, gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))' }}>
        <div className="card stat-card" style={{ borderTop: '4px solid #EF233C' }}>
          <div className="stat-icon" style={{ background: 'rgba(239, 35, 60, 0.1)', color: '#EF233C' }}><MdTrendingUp /></div>
          <div className="stat-label">Total Lifetime Earnings</div>
          <div className="stat-value">{formatINR(lifetimeEarnings)}</div>
          <div className="stat-desc">All time earnings</div>
        </div>
        <div className="card stat-card" style={{ borderTop: '4px solid #2563EB' }}>
          <div className="stat-icon" style={{ background: 'rgba(37, 99, 235, 0.1)', color: '#2563EB' }}><MdAccountBalanceWallet /></div>
          <div className="stat-label">Wallet Balance</div>
          <div className="stat-value">{formatINR(availableBalance)}</div>
          <div className="stat-desc" style={{ color: '#2563EB' }}>Available balance</div>
        </div>
        <div className="card stat-card" style={{ borderTop: '4px solid #F59E0B' }}>
          <div className="stat-icon" style={{ background: 'rgba(245, 158, 11, 0.1)', color: '#F59E0B' }}><MdMonetizationOn /></div>
          <div className="stat-label">Pending Earnings</div>
          <div className="stat-value">{formatINR(pendingEarnings)}</div>
          <div className="stat-desc" style={{ color: '#F59E0B' }}>Yet to be added to wallet</div>
        </div>
        <div className="card stat-card" style={{ borderTop: '4px solid #10B981' }}>
          <div className="stat-icon" style={{ background: 'rgba(16, 185, 129, 0.1)', color: '#10B981' }}><MdCheckCircle /></div>
          <div className="stat-label">Total Paid</div>
          <div className="stat-value">{formatINR(totalPaid)}</div>
          <div className="stat-desc" style={{ color: '#10B981' }}>Total amount paid</div>
        </div>
        <div className="card stat-card" style={{ borderTop: '4px solid #F59E0B' }}>
          <div className="stat-icon" style={{ background: 'rgba(245, 158, 11, 0.1)', color: '#F59E0B' }}><MdPendingActions /></div>
          <div className="stat-label">Pending Payout Requests</div>
          <div className="stat-value">{formatINR(pendingRequestsAmount)}</div>
          <div className="stat-desc" style={{ color: '#F59E0B' }}>{pendingRequestsCount} requests pending</div>
        </div>
        <div className="card stat-card" style={{ borderTop: '4px solid #8B5CF6' }}>
          <div className="stat-icon" style={{ background: 'rgba(139, 92, 246, 0.1)', color: '#8B5CF6' }}><MdPayment /></div>
          <div className="stat-label">Completed Payouts</div>
          <div className="stat-value">{formatINR(totalPaid)}</div>
          <div className="stat-desc" style={{ color: '#8B5CF6' }}>{completedPayoutsCount} successful payouts</div>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 24, marginBottom: 24 }}>
        {/* Wallet Summary */}
        <div className="card">
          <div className="section-header"><h3>Wallet Summary</h3></div>
          <div style={{ display: 'flex', justifyContent: 'space-between', textAlign: 'center', marginBottom: 20 }}>
            <div>
              <div style={{ width: 40, height: 40, background: '#E0F2FE', color: '#0284C7', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 8px' }}><MdAccountBalanceWallet size={20} /></div>
              <div style={{ fontSize: 12, color: '#64748B' }}>Available Balance</div>
              <div style={{ fontWeight: 600, color: '#10B981' }}>{formatINR(availableBalance)}</div>
            </div>
            <div>
              <div style={{ width: 40, height: 40, background: '#D1FAE5', color: '#059669', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 8px' }}><MdTrendingUp size={20} /></div>
              <div style={{ fontSize: 12, color: '#64748B' }}>Total Credits</div>
              <div style={{ fontWeight: 600, color: '#10B981' }}>{formatINR(lifetimeEarnings)}</div>
            </div>
            <div>
              <div style={{ width: 40, height: 40, background: '#FEE2E2', color: '#DC2626', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 8px' }}><MdTrendingUp size={20} style={{ transform: 'rotate(180deg)' }} /></div>
              <div style={{ fontSize: 12, color: '#64748B' }}>Total Debits</div>
              <div style={{ fontWeight: 600 }}>{formatINR(totalPaid + pendingRequestsAmount)}</div>
            </div>
          </div>
          <hr style={{ border: 'none', borderTop: '1px solid #F1F5F9', margin: '15px 0' }} />
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 13, color: '#475569', marginBottom: 10 }}>
            <MdPayment size={18} /> Last Transaction: {ledger[0] ? formatDate(ledger[0].created_at) : 'N/A'}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 13, color: '#475569' }}>
            <MdCheckCircle size={18} /> Last Updated: {ledger[0] ? formatDate(ledger[0].created_at) : 'N/A'}
          </div>
        </div>

        {/* Payout Requests */}
        <div className="card">
          <div className="section-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h3 style={{ margin: 0 }}>Payout Requests</h3>
            <button className="btn btn-sm btn-ghost" style={{ border: '1px solid #E2E8F0', padding: '4px 12px' }} onClick={() => navigate('/withdrawals')}>View All</button>
          </div>
          <div className="table-wrap" style={{ maxHeight: 220, overflowY: 'auto' }}>
            <table className="table" style={{ fontSize: 13 }}>
              <thead>
                <tr><th>Request ID</th><th>Date</th><th>Amount</th><th>Method</th><th>Status</th><th>Processed Date</th></tr>
              </thead>
              <tbody>
                {withdrawals.slice(0, 5).map(w => (
                  <tr key={w.id}>
                    <td>PRQ-{w.id.toString().padStart(5, '0')}</td>
                    <td>{formatDate(w.created_at)}</td>
                    <td>{formatINR(w.amount)}</td>
                    <td>{w.payout_snapshot?.method?.toUpperCase() || '-'}</td>
                    <td>
                      <span className={`badge ${w.status === 'paid' ? 'badge-success' : w.status === 'rejected' ? 'badge-danger' : 'badge-warning'}`}>
                        {w.status}
                      </span>
                    </td>
                    <td>{w.processed_at ? formatDate(w.processed_at) : '-'}</td>
                  </tr>
                ))}
                {withdrawals.length === 0 && <tr><td colSpan="6" style={{ textAlign: 'center', padding: 20 }}>No payout requests</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Wallet Transaction History */}
      <div className="card" style={{ marginBottom: 24 }}>
        <div className="section-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h3 style={{ margin: 0 }}>Wallet Transaction History</h3>
          <button className="btn btn-sm btn-ghost" style={{ display: 'none', border: '1px solid #E2E8F0', padding: '4px 12px' }} onClick={() => toast('Transaction history details page coming soon!')}>View All</button>
        </div>
        <div className="table-wrap" style={{ maxHeight: 300, overflowY: 'auto' }}>
          <table className="table" style={{ fontSize: 13 }}>
            <thead>
              <tr><th>Date & Time</th><th>Transaction Type</th><th>Credit (₹)</th><th>Debit (₹)</th><th>Description</th><th>Reference ID</th></tr>
            </thead>
            <tbody>
              {ledger.slice(0, 20).map(l => (
                <tr key={l.id}>
                  <td>{formatDateTime(l.createdAt || l.created_at)}</td>
                  <td>{formatTxType(l.type, l.reference_type)}</td>
                  <td style={{ color: '#10B981' }}>{l.type === 'credit' ? l.amount : '-'}</td>
                  <td style={{ color: '#DC2626' }}>{l.type === 'debit' ? l.amount : '-'}</td>
                  <td>{l.description || '-'}</td>
                  <td>TXN-{l.id.toString().padStart(6, '0')}</td>
                </tr>
              ))}
              {ledger.length === 0 && <tr><td colSpan="6" style={{ textAlign: 'center', padding: 20 }}>No transactions yet</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      {/* Daily Earnings Analytics */}
      <div className="card" style={{ marginBottom: 24 }}>
        <div className="section-header"><h3>Daily Earnings</h3></div>
        <div style={{ height: 350, marginTop: 20 }}>
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={chartData} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E2E8F0" />
              <XAxis dataKey="date" tick={{ fontSize: 12, fill: '#64748B' }} tickFormatter={(val) => new Date(val).toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })} />
              <YAxis yAxisId="left" tick={{ fontSize: 12, fill: '#64748B' }} />
              <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 12, fill: '#64748B' }} tickFormatter={(v) => `₹${v}`} />
              <Tooltip 
                contentStyle={{ borderRadius: 8, border: 'none', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)' }}
                labelFormatter={(val) => new Date(val).toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' })}
              />
              <Legend />
              <Bar yAxisId="left" dataKey="articles" name="Articles Published" fill="#3B82F6" radius={[4, 4, 0, 0]} maxBarSize={40} />
              <Line yAxisId="left" type="monotone" dataKey="reads" name="Reads" stroke="#10B981" strokeWidth={2} dot={{ r: 4, fill: '#10B981', strokeWidth: 0 }} />
              <Line yAxisId="right" type="monotone" dataKey="earnings" name="Earnings (₹)" stroke="#F59E0B" strokeWidth={2} dot={{ r: 4, fill: '#F59E0B', strokeWidth: 0 }} />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 24, marginBottom: 24 }}>
        {/* Top Earning Articles */}
        <div className="card">
          <div className="section-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h3 style={{ margin: 0 }}>Top Earning Articles</h3>
            <button className="btn btn-sm btn-ghost" style={{ border: '1px solid #E2E8F0', padding: '4px 12px' }} onClick={() => navigate('/articles?top_earning=true&contributor_id=' + selectedContributorId)}>View All</button>
          </div>
          <div className="table-wrap" style={{ maxHeight: 350, overflowY: 'auto' }}>
            <table className="table" style={{ fontSize: 13 }}>
              <thead>
                <tr><th>#</th><th>Article Title</th><th>Published Date</th><th>Reads</th><th>Earnings (₹)</th></tr>
              </thead>
              <tbody>
                {topArticles.map((art, idx) => (
                  <tr key={art.id}>
                    <td>{idx + 1}</td>
                    <td style={{ maxWidth: 200, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{art.title}</td>
                    <td>{formatDate(art.published_at)}</td>
                    <td>{art.read_count}</td>
                    <td style={{ fontWeight: 600 }}>{formatINR(art.earnings)}</td>
                  </tr>
                ))}
                {topArticles.length === 0 && <tr><td colSpan="5" style={{ textAlign: 'center', padding: 20 }}>No articles with earnings found</td></tr>}
              </tbody>
            </table>
          </div>
        </div>

        {/* Payment Information */}
        <div className="card">
          <div className="section-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h3 style={{ margin: 0 }}>Payment Information</h3>
            <button className="btn btn-sm btn-ghost" style={{ border: '1px solid #E2E8F0', padding: '4px 12px' }} onClick={() => navigate('/contributors')}>✏ Edit</button>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 15, fontSize: 13 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid #F1F5F9', paddingBottom: 8 }}>
              <span style={{ color: '#64748B' }}>Payment Method</span>
              <span style={{ fontWeight: 500 }}>{payoutInfo.method === 'bank' ? 'Bank Transfer' : payoutInfo.method?.toUpperCase() || '-'}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid #F1F5F9', paddingBottom: 8 }}>
              <span style={{ color: '#64748B' }}>Bank Name</span>
              <span style={{ fontWeight: 500 }}>{payoutInfo.method === 'bank' ? 'Bank Account' : '-'}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid #F1F5F9', paddingBottom: 8 }}>
              <span style={{ color: '#64748B' }}>Account Holder Name</span>
              <span style={{ fontWeight: 500 }}>{payoutInfo.bank_account_name || '-'}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid #F1F5F9', paddingBottom: 8 }}>
              <span style={{ color: '#64748B' }}>Account Number</span>
              <span style={{ fontWeight: 500 }}>{payoutInfo.bank_account_number ? `**** **** ${payoutInfo.bank_account_number.slice(-4)}` : '-'}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid #F1F5F9', paddingBottom: 8 }}>
              <span style={{ color: '#64748B' }}>IFSC Code</span>
              <span style={{ fontWeight: 500 }}>{payoutInfo.bank_ifsc || '-'}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid #F1F5F9', paddingBottom: 8 }}>
              <span style={{ color: '#64748B' }}>UPI ID</span>
              <span style={{ fontWeight: 500 }}>{payoutInfo.upi_id || '-'}</span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ color: '#64748B' }}>Preferred Payment Method</span>
              <span className="badge badge-success">{payoutInfo.method === 'bank' ? 'Bank Transfer' : payoutInfo.method?.toUpperCase() || '-'}</span>
            </div>
          </div>
        </div>
      </div>
      
      {/* Monthly Earnings Summary Table */}
      <div className="card">
        <div className="section-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h3 style={{ margin: 0 }}>Earnings by Month</h3>
          <button className="btn btn-sm btn-ghost" style={{ display: 'none', border: '1px solid #E2E8F0', padding: '4px 12px' }} onClick={() => toast('Monthly earnings report coming soon!')}>View All</button>
        </div>
        <div className="table-wrap">
          <table className="table" style={{ fontSize: 13 }}>
            <thead>
              <tr><th>Month</th><th>Articles</th><th>Reads</th><th>Earnings (₹)</th><th>Paid Amount (₹)</th></tr>
            </thead>
            <tbody>
              {analytics?.monthly?.earnings?.map(m => {
                const monthArticles = analytics.monthly.articles.find(a => a.month === m.month)?.articles || 0;
                const monthReads = analytics.monthly.reads.find(a => a.month === m.month)?.reads || 0;
                const monthPaid = analytics.monthly.withdrawals.find(a => a.month === m.month)?.paid_amount || 0;
                
                return (
                  <tr key={m.month}>
                    <td style={{ fontWeight: 500 }}>{new Date(m.month + '-01').toLocaleDateString('en-GB', { month: 'long', year: 'numeric' })}</td>
                    <td>{monthArticles}</td>
                    <td>{monthReads}</td>
                    <td style={{ color: '#10B981', fontWeight: 600 }}>{formatINR(m.earnings)}</td>
                    <td style={{ color: '#EF233C', fontWeight: 600 }}>{formatINR(monthPaid)}</td>
                  </tr>
                );
              })}
              {(!analytics?.monthly?.earnings || analytics.monthly.earnings.length === 0) && (
                <tr><td colSpan="5" style={{ textAlign: 'center', padding: 20 }}>No data available</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
        </>
      )}
    </div>
  );
}
